import { Component, input, model, OnDestroy, OnInit, output, ViewChild } from '@angular/core';

import { AsyncValidatorFn, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';

import { TranslocoService } from '@jsverse/transloco';
import { Subject, takeUntil } from 'rxjs';
import { DrawerComponent, EnumErrorStates, ErrorObject } from '@inmoment/the-kitchen';

import { AppService } from 'app/app.service';
import { GroupService } from 'app/providers/semantria-api/group.service';
import {
  EnableClassTelemetry,
  EnableComponentTelemetry,
  injectComponentTelemetryService,
} from 'app/services/otel.service';
import { ErrorObjectUtils } from 'app/utils/error-object.utils';
import {
  ConfigurationService,
  SemantriaConfiguration,
} from '../../../../providers/semantria-api/configuration.service';
import {
  LanguageTemplate,
  LanguageTemplateService,
} from '../../../../providers/semantria-api/language-template.service';
import {
  BriefIndustryPack,
  SemantriaAccountService,
} from '../../../../providers/semantria-api/semantria-account.service';
import { AlertService } from '../../../../services/alert.service';

import { ConfigurationIncludeData } from '../configuration-content-form/configuration-content-form.component';
import {
  ConfigType,
  ImportConfigurationDialogOptions,
} from '../import-into-configuration-drawer/import-into-configuration-drawer.component';
import { configNameValidatorFactoryAsync, urlValidator } from '../../configuration-utils';

export interface CreateConfigurationDialogOptions extends ImportConfigurationDialogOptions {
  existingConfigs: SemantriaConfiguration[];
  fragment?: string;
}

interface ConfigTemplateNode {
  id: string;
  name: string;
  languageTemplates: LanguageTemplate[];
  configurations: SemantriaConfiguration[];
  industryPacks: BriefIndustryPack[];
}

@EnableClassTelemetry()
@Component({
  selector: 'app-create-configuration-drawer',
  templateUrl: './create-configuration-drawer.component.html',
  styleUrls: ['./create-configuration-drawer.component.scss'],
  standalone: false,
})
export class CreateConfigurationDrawerComponent implements OnInit, OnDestroy {
  @EnableComponentTelemetry() otelService = injectComponentTelemetryService();
  readonly groupId = input<string>();
  readonly open = input<boolean>();
  readonly existingConfigs = input<SemantriaConfiguration[]>();
  readonly inputFile = input<any>();
  readonly configDto = model<Partial<SemantriaConfiguration>>();
  readonly fragment = input<string>();
  readonly configType = input<ConfigType>();

  readonly close = output<{
    configuration: SemantriaConfiguration | undefined;
    fragment?: string;
  }>();

  @ViewChild(DrawerComponent) drawer: DrawerComponent;

  readonly contentValues = [
    {
      label: '',
      tooltip: '',
      presets: {
        alphanumeric_threshold: 50,
        entities_threshold: 55,
      },
    },
    {
      label: '',
      tooltip: '',
      presets: {
        alphanumeric_threshold: 80,
        entities_threshold: 65,
      },
    },
    {
      label: '',
      tooltip: '',
      presets: {
        alphanumeric_threshold: 25,
        entities_threshold: 55,
        settings: ['ONE_SENTENCE_MODE'],
      },
    },
  ];

  readonly responseTypeValues = [
    {
      label: '',
      value: 'POLLING',
      tooltip: '',
    },
    {
      label: '',
      value: 'AUTORESPONSE',
      tooltip: '',
    },
    {
      label: '',
      value: 'CALLBACK',
      tooltip: '',
    },
  ];

  accountFeatures: string[] = [];
  missingAccountFeatures: string[] = [];

  configForm: UntypedFormGroup;
  configIncludeData: ConfigurationIncludeData;
  configNameStatus: ErrorObject = { state: EnumErrorStates.SUCCESS };

  configTemplates: ConfigTemplateNode[] = [];
  filteredLanguageTemplates: LanguageTemplate[] = [];
  filteredConfigurations: SemantriaConfiguration[] = [];
  filteredIndustryPacks: BriefIndustryPack[] = [];
  filteredStandardConfigs: {
    language: LanguageTemplate[];
    configuration: SemantriaConfiguration[];
    pack: BriefIndustryPack[];
  } = {
    language: [],
    configuration: [],
    pack: [],
  };
  filteredLayeredConfigs: {
    language: LanguageTemplate[];
    configuration: SemantriaConfiguration[];
    pack: BriefIndustryPack[];
  } = {
    language: [],
    configuration: [],
    pack: [],
  };
  readonly entryOrder = (_a, _b) => 0;

  languages: { id: string; name: string }[] = [];
  languageLabelFormatter = (value) => value.id.toUpperCase() + ' ' + value.name;
  selectedLanguage: { id: string; name: string };
  selectedTemplateType: 'language' | 'configuration' | 'pack' | 'configStandard' | 'configLayered';

  fileName: string;
  fileType: string;

  isSaving: boolean;

  nameValidator: AsyncValidatorFn;

  isTagEditorEditable: boolean;

  private unsub$ = new Subject<void>();

  constructor(
    private formBuilder: UntypedFormBuilder,
    private accountService: SemantriaAccountService,
    private configService: ConfigurationService,
    private groupService: GroupService,
    private languageTemplateService: LanguageTemplateService,
    private alertService: AlertService,
    private translocoService: TranslocoService
  ) {
    this.configForm = this.formBuilder.group({
      configName: ['', Validators.required],
      configNotes: [''],
      language: ['', Validators.required],
      configBase: [[], Validators.required],
      contentType: [this.contentValues[0], Validators.required],
      responseType: ['POLLING', Validators.required],
      callbackUrl: ['', Validators.compose([Validators.required, urlValidator])],
      tagEditor: [''],
    });

    this.configForm.controls.callbackUrl.disable();

    this.configForm.controls.responseType.valueChanges.subscribe((val) => {
      if (val === 'POLLING' || val === 'AUTORESPONSE') {
        this.configForm.controls.callbackUrl.disable();
      } else {
        this.configForm.controls.callbackUrl.enable();
      }
    });
  }

  async ngOnInit() {
    AppService.hideBodyOverflow();

    this.translocoService
      .selectTranslateObject('configurations.create-drawer.content-types')
      .pipe(takeUntil(this.unsub$))
      .subscribe((translations) => {
        this.contentValues[0].label = translations['support-label'];
        this.contentValues[0].tooltip = translations['support-tooltip'];
        this.contentValues[1].label = translations['news-label'];
        this.contentValues[1].tooltip = translations['news-tooltip'];
        this.contentValues[2].label = translations['tweets-label'];
        this.contentValues[2].tooltip = translations['tweets-tooltip'];
      });

    this.translocoService
      .selectTranslateObject('configurations.create-drawer.response-types')
      .pipe(takeUntil(this.unsub$))
      .subscribe((translations) => {
        this.responseTypeValues[0].label = translations['polling-label'];
        this.responseTypeValues[0].tooltip = translations['polling-tooltip'];
        this.responseTypeValues[1].label = translations['auto-response-label'];
        this.responseTypeValues[1].tooltip = translations['auto-response-tooltip'];
        this.responseTypeValues[2].label = translations['callback-label'];
        this.responseTypeValues[2].tooltip = translations['callback-tooltip'];
      });

    if (!this.configDto()) {
      const configDto = {
        tags: [],
        layer: this.configType() === 'layered',
      };
      this.configDto.set(configDto);
      const accountDetails = await this.accountService.sub.latest();
      this.accountFeatures = accountDetails.features;
      this.configTemplates = accountDetails.languages
        .map((lang) => ({
          id: lang.id,
          name: lang.name,
          industryPacks: accountDetails.industry_packs
            ? accountDetails.industry_packs.filter((p) => p.language_id === lang.id)
            : [],
          languageTemplates: [],
          configurations: [],
        }))
        .sort((l1, l2) => l1.name.localeCompare(l2.name));

      this.languages = this.configTemplates
        .map((ct) => ({ id: ct.id, name: ct.name }))
        .sort((a, b) => a.name.localeCompare(b.name));

      const fillTemplateOptions = (field) => {
        return (res) => {
          for (const ct of this.configTemplates) {
            ct[field] = res.filter((lt) => lt.language_id === ct.id && !lt.id.endsWith('prm'));
          }

          const defaultLanguage = this.languages.find((l) => l.id === 'en') || this.languages[0];

          this.onLanguageChanged(defaultLanguage);
        };
      };

      await Promise.all([
        this.languageTemplateService
          .getLanguageTemplates()
          .then(fillTemplateOptions('languageTemplates')),
        this.configService
          .getConfigurations(false, this.groupId())
          .then(fillTemplateOptions('configurations')),
      ]);
    } else {
      if (this.inputFile()) {
        const parts = this.inputFile().name.split('.');
        this.fileName = parts[0];
        this.fileType = parts[parts.length - 1];
      }

      this.initFormFromDto();
    }
    this.getTagEditableStatus();
  }

  ngOnDestroy() {
    this.unsub$.next();
    this.unsub$.complete();

    AppService.showBodyOverflow();
  }

  private initFormFromDto() {
    this.setConfigNameValidator();

    this.configDto.set({ ...this.configDto(), tags: this.configDto().tags || [] });
    this.configForm.controls.configName.setValue(this.configDto().name);
    this.configForm.controls.configNotes.setValue(this.configDto().notes);
    this.configForm.controls.language.setValue(this.configDto().language_id);
    this.configForm.controls.configBase.setValue([
      this.configDto().template_config || this.configDto().language_id,
    ]);
    this.configForm.controls.contentType.disable();
    this.configForm.controls.callbackUrl.disable();

    if (this.configDto().is_autoresponse_enabled) {
      this.configForm.controls.responseType.setValue('AUTORESPONSE');
    } else if (this.configDto().callback_url) {
      this.configForm.controls.responseType.setValue('CALLBACK');
      this.configForm.controls.callbackUrl.setValue(this.configDto().callback_url);
      this.configForm.controls.callbackUrl.enable();
    } else {
      this.configForm.controls.responseType.setValue('POLLING');
    }

    this.configForm.controls.configName.markAsDirty();
    this.onValidateConfigName();
  }

  onTagAdded(tag) {
    this.configDto.set({ ...this.configDto(), tags: [...this.configDto().tags, tag] });
  }

  onTagRemoved(index) {
    const tags = [...this.configDto().tags];
    tags.splice(index, 1);
    this.configDto.set({ ...this.configDto(), tags });
  }

  onLanguageChanged(lang) {
    this.selectedLanguage = lang;

    this.setConfigNameValidator();

    const templatesForLanguage = this.configTemplates.find((ct) => ct.id === lang.id);
    this.filteredLanguageTemplates = templatesForLanguage.languageTemplates;
    this.filteredConfigurations = templatesForLanguage.configurations;
    this.filteredIndustryPacks = templatesForLanguage.industryPacks.sort((a, b) =>
      a.name.localeCompare(b.name, undefined, { sensitivity: 'accent' })
    );

    this.filteredStandardConfigs.language = this.filteredLanguageTemplates;
    this.filteredStandardConfigs.configuration = this.filteredConfigurations.filter(
      (c) => !c.layer
    );
    this.filteredStandardConfigs.pack = this.filteredIndustryPacks;

    this.filteredLayeredConfigs.configuration = this.filteredConfigurations.filter((c) => c.layer);

    if (this.configType() === 'standard') {
      const baseTemplate = templatesForLanguage.languageTemplates.find(
        (lt) => lt.id === this.selectedLanguage.id
      );

      if (baseTemplate) {
        this.selectTemplate('language', baseTemplate?.language_id, baseTemplate?.id);
      }
    } else {
      this.configBaseControl.setValue([]);
    }
  }

  getTagEditableStatus() {
    setTimeout(() => {
      this.isTagEditorEditable = this.configIncludeData?.includes?.tags !== false;
    }, 0);
  }

  private setConfigNameValidator() {
    this.configForm.controls.configName.clearAsyncValidators();
    this.nameValidator = configNameValidatorFactoryAsync(
      this.configService,
      undefined,
      this.existingConfigs()
    );
    this.configForm.controls.configName.addAsyncValidators(this.nameValidator);
    this.configForm.controls.configName.updateValueAndValidity();
  }

  isDataSourceEmpty(templateDs) {
    return !Object.keys(templateDs).reduce((prev, curr) => prev + templateDs[curr].length, 0);
  }

  selectTemplate(type, languageId, templateId) {
    this.selectedTemplateType = type;
    this.configForm.controls.language.setValue(languageId);
    this.configBaseControl.setValue([templateId]);
  }

  toggleTemplate(languageId, templateId) {
    let configBaseValue = this.configBaseControl.value;
    if (configBaseValue.includes(templateId)) {
      configBaseValue = configBaseValue.filter((id) => id !== templateId);
    } else {
      configBaseValue.push(templateId);
    }

    this.configForm.controls.language.setValue(languageId);
    this.configBaseControl.setValue(configBaseValue);
  }

  isTemplateSelected(languageId, templateId) {
    return (
      this.configForm.controls.language.value === languageId &&
      this.configBaseControl.value.includes(templateId)
    );
  }

  onConfigIncludeChanged($event) {
    this.configIncludeData = $event;
    this.getTagEditableStatus();
    if (this.configIncludeData.includes.notes) {
      this.configForm.controls.configNotes.enable();
    } else {
      this.configForm.controls.configNotes.disable();
    }
  }

  onAccountFeatureChecked($event) {
    this.missingAccountFeatures = $event;
  }

  createOrImport() {
    if (this.inputFile()) {
      return this.import();
    }
    this.create();
  }

  async create() {
    this.isSaving = true;
    this.configForm.disable();

    const configDto: Partial<SemantriaConfiguration> = {
      name: this.configForm.controls.configName.value,
      language_id: this.configForm.controls.language.value,
      settings: [],
      tags: this.configDto().tags,
      notes: this.configForm.controls.configNotes.value,
    };

    if (this.configType() === 'standard') {
      configDto.cloned_from = this.configBaseControl.value[0];
    } else {
      const orderingHelper = [
        ...this.filteredStandardConfigs.language,
        ...this.filteredStandardConfigs.configuration,
        ...this.filteredStandardConfigs.pack,
        ...this.filteredLayeredConfigs.language,
        ...this.filteredLayeredConfigs.configuration,
        ...this.filteredLayeredConfigs.pack,
      ].map((c) => c.id);

      configDto.layer = true;
      configDto.parents = this.configBaseControl.value.sort(
        (a, b) => orderingHelper.indexOf(a) - orderingHelper.indexOf(b)
      );
    }

    // set preset values based on the content type (can be prop or setting)
    const configPresets = this.configForm.controls.contentType.value.presets;
    Object.keys(configPresets).forEach((p) => {
      if (Array.isArray(configPresets[p])) {
        configDto.settings.concat(configPresets[p]);
      } else {
        configDto[p] = configPresets[p];
      }
    });

    // set response type and callback url
    if (this.configForm.controls.responseType.value === 'AUTORESPONSE') {
      configDto.is_autoresponse_enabled = true;
    } else if (this.configForm.controls.responseType.value === 'CALLBACK') {
      configDto.callback_url = this.configForm.controls.callbackUrl.value;
    }

    // set default value for sentiment_sections
    if (!this.configDto().sentiment_sections) {
      this.configDto.update((prev) => ({ ...prev, sentiment_sections: ['ALL'] }));
    }

    this.configDto.set(configDto);

    try {
      const configuration = await this.configService.createConfiguration(
        this.configDto(),
        this.groupId()
      );
      await this.groupService.refreshAndSelectActiveGroup(this.groupId(), true);

      this.alertService.showToast(
        this.translocoService.translate('configurations.create-drawer.create-success', {
          configuration: this.configDto().name,
        })
      );

      this.close.emit({ configuration });
    } catch (e) {
      this.isSaving = false;
      this.configForm.enable();
      if (this.configForm.controls.responseType.value !== 'CALLBACK') {
        this.configForm.controls.callbackUrl.disable();
      }

      if (e.status === 402) {
        this.alertService.showErrorToast(e.error.details);
      } else {
        const msg = e.error.error || Object.values(e.error['violations'])[0];
        this.alertService.showErrorToast(
          `${this.translocoService.translate('configurations.create-drawer.create-failed', {
            configuration: this.configDto().name,
          })} : ${msg}`
        );
      }
    }
  }

  async import() {
    this.isSaving = true;

    const configDtoToSend = JSON.parse(JSON.stringify(this.configDto()));

    try {
      configDtoToSend.name = this.configForm.controls.configName.value;
      configDtoToSend.notes = this.configForm.controls.configNotes.value;

      if (!this.configIncludeData.includes.notes) {
        configDtoToSend.notes = undefined;
      }

      if (!this.configIncludeData.includes.tags) {
        configDtoToSend.tags = undefined;
      }

      Object.keys(this.configIncludeData.includes)
        .filter((k) => k !== 'notes' && k !== 'tags')
        .filter((k) => !this.configIncludeData.includes[k])
        .forEach((k) => (configDtoToSend[k] = undefined));

      const configuration: any = await this.configService.importConfiguration(
        configDtoToSend,
        this.groupId()
      );
      await this.groupService.refreshAndSelectActiveGroup();

      this.alertService.showToast(
        this.translocoService.translate('configurations.create-drawer.import-success', {
          configuration: configDtoToSend.name,
        })
      );

      this.close.emit({ configuration, fragment: this.fragment() });
    } catch (e) {
      this.isSaving = false;
      if (e.status === 402) {
        this.alertService.showErrorToast(e.error.details || e.error.error);
      } else {
        const msg = e.error.error || Object.values(e.error['violations'])[0];
        this.alertService.showErrorToast(
          `${this.translocoService.translate('configurations.create-drawer.import-failed', {
            configuration: configDtoToSend.name,
          })} : ${msg}`
        );
      }
    }
  }

  cancel(restart?) {
    this.close.emit(restart);
  }

  get configBaseControl() {
    return this.configForm.controls.configBase;
  }

  get configName() {
    return this.configForm.controls.configName;
  }

  onValidateConfigName() {
    if (this.configName.hasError('required')) {
      ErrorObjectUtils.setError(
        this.configNameStatus,
        this.translocoService.translate('configurations.create-drawer.name-errors.required')
      );
      return;
    }

    if (this.configName.hasError('minLength')) {
      ErrorObjectUtils.setError(
        this.configNameStatus,
        this.translocoService.translate('configurations.create-drawer.name-errors.min-length', {
          charLength: this.configName.errors.minLength.minLength,
        })
      );
      return;
    }

    if (this.configName.hasError('maxLength')) {
      ErrorObjectUtils.setError(
        this.configNameStatus,
        this.translocoService.translate('configurations.create-drawer.name-errors.max-length', {
          charLength: this.configName.errors.maxLength.maxLength,
        })
      );
      return;
    }

    if (this.configName.hasError('nameAlreadyExists')) {
      ErrorObjectUtils.setError(
        this.configNameStatus,
        this.translocoService.translate('configurations.create-drawer.name-errors.exists')
      );
      return;
    }

    ErrorObjectUtils.setNoError(this.configNameStatus);
  }
}
